import React, { useMemo } from "react";
import ResponsiveLineChart from "./components/ResponsiveLineChart";
import DashboardWidgetCard from "./components/DashboardWidgetCard";
import DashboardCardHeader from "./components/DashboardCardHeader";
import ToggleButtonsTrasnferList from "./components/ToggleButton";

import { makeStyles, Tooltip } from "@material-ui/core";
import SelectInput from "./components/SelectInput";
import SavedDocInbox from "./icons/SavedDocInbox";
import HeaderAndValue from "./components/HeaderAndValue";

import useFetch from "./hooks/useFetch";
import Loading from "./components/Loading";
import {
  formatDate,
  formatToCommaString,
  formatToCommaStringDecPoint,
  numDifferentiation,
} from "./helper/format";
import DaywiseActiveMerchant from "./DaywiseActiveMerchant";
import temp_data from "./temp";

const useStyle = makeStyles((theme) => {
  return {
    card: { margin: theme.spacing(2, 0) },
    cardHeader: {
      borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
      paddingBottom: theme.spacing(2),
      marginTop: theme.spacing(0.5),
    },
    updateDate: {
      color: "rgba(54, 43, 115, 0.56)",
      margin: "0",
      fontSize: 12,
    },
    container: {
      display: "flex",
      width: "100%",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    widget: {
      background: "white",
      borderRadius: theme.spacing(1.5),
      margin: theme.spacing(0, 1, 2),
      width: "calc(25% - 16px)",
      textAlign: "center",
      padding: theme.spacing(2),
      [theme.breakpoints.down("md")]: {
        width: "calc(50% - 16px)",
      },
    },
    filtersContent: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down("md")]: {
        flexWrap: "wrap",
      },
    },
    chartContainer: {
      height: 440,
      marginTop: theme.spacing(2),
      overflowX: "auto",
      position: "relative",
    },
    chartWrapper: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      height: 420,
      minWidth: 1000,
    },
    paymentForm: {
      margin: 8,
      width: "calc(35%)",
      [theme.breakpoints.down("md")]: {
        width: "calc(70%)",
      },
    },
    tooltip: {
      fontSize: 16,
    },
  };
});

const options = [
  {
    label: "Eligible Payment",
    value: "ELIGIBLE_PMNT",
  },
  {
    label: "Payment",
    value: "PAYMENT",
  },
  {
    label: "M2A",
    value: "M2A",
  },
  {
    label: "M2P",
    value: "M2P",
  },
  {
    label: "M2M",
    value: "M2M_TRANSFER",
  },
  {
    label: "Mobile Recharge",
    value: "MOBILE_RECHARGE",
  },
  {
    label: "Bill Payment Count",
    value: "BILLPAY",
  },
  {
    label: "Active Merchant Any Amount",
    value: "ACTIVE_MERCHANT_ANY_AMT",
  },
  {
    label: "Active Merchant Qualified",
    value: "ACTIVE_MERCHANT_QUALIFIED",
  },
];

export default function PerformanceOverview({ geoFilters }) {
  const params = useMemo(() => geoFilters, [geoFilters]);
  const classes = useStyle();
  const { data, isLoading } = useFetch(
    "/reports/dashboard/performance-overview",
    params
  );
  // const data = temp_data;
  // const isLoading = false;
  const [tabIndex, setTabIndex] = React.useState(0);
  const [paymentTabIndex, setPaymentTabIndex] = React.useState(0);
  const [paymentValue, setPaymentValue] = React.useState("ELIGIBLE_PMNT");

  const tabConfig = useMemo(() => {
    let _tabConfig = [
      { label: "MTD", id: "_MTD" },
      { label: "Daily", id: "_LASTDAY" },
    ];
    if (
      ["BILLPAY", "MOBILE_RECHARGE", "ELIGIBLE_PMNT"].includes(paymentValue)
    ) {
      return [{ label: "MTD", id: "_MTD" }];
    }

    if (paymentValue === "PAYMENT") {
      _tabConfig[1].id = "";
    } else if (
      ["ACTIVE_MERCHANT_ANY_AMT", "ACTIVE_MERCHANT_QUALIFIED"].includes(
        paymentValue
      )
    ) {
      return [{ label: "MTD", id: "" }];
    }

    return _tabConfig;
  }, [paymentValue]);

  const paymentTabConfig = useMemo(() => {
    const _paymentTabConfig = [
      { label: "Amount", id: "_AMT" },
      { label: "Count", id: "_CNT" },
      { label: "Customer Count", id: "_CUSTOMER_CNT" },
    ];

    if (["PAYMENT", "M2M"].includes(paymentValue)) {
      return _paymentTabConfig;
    } else if (
      ["ACTIVE_MERCHANT_ANY_AMT", "ACTIVE_MERCHANT_QUALIFIED"].includes(
        paymentValue
      )
    ) {
      return [
        { label: "Count", id: "" },
        { label: "Eligible", id: "_ELIGIBLE" },
      ];
    } else if (paymentValue === "ELIGIBLE_PMNT") {
      return [
        { label: "Amount", id: "_AMT" },
        { label: "Count", id: "_CNT" },
        { label: "Customer Count", id: "_DISTINCT_CUST" },
      ];
    }

    return [
      { label: "Amount", id: "_AMT" },
      { label: "Count", id: "_CNT" },
    ];
  }, [paymentValue]);

  const onTabChange = (index) => {
    if (index == null) return;
    if (index >= 0 && index < tabConfig.length) {
      setTabIndex(index);
    }
  };

  const onPaymentTabChange = (index) => {
    if (index == null) return;
    if (index >= 0 && index < paymentTabConfig.length) {
      setPaymentTabIndex(index);
    }
  };

  const getToday = () => {
    const id =
      paymentValue +
      paymentTabConfig[paymentTabIndex].id +
      tabConfig[tabIndex].id;

    if (tabConfig[tabIndex].label === "MTD") {
      const label = "Current Month";

      const value =
        data.current_month.length > 0 ? data.current_month.at(-1)[id] : "-";

      return {
        label,
        value: numDifferentiation(value),
        unformattedValue: value,
        loaclaizedValue: formatToCommaString(value),
      };
    } else {
      const label = "Yesterday (D-1)";

      if (data.current_month?.length === 0) {
        return {
          label,
          value: "-",
        };
      }

      const today = data.current_month.at(-1)[id];

      return {
        label,
        value: numDifferentiation(today),
        unformattedValue: today,
        loaclaizedValue: formatToCommaString(today),
      };
    }
  };

  const getYesterday = () => {
    const id =
      paymentValue +
      paymentTabConfig[paymentTabIndex].id +
      tabConfig[tabIndex].id;

    if (tabConfig[tabIndex].label === "MTD") {
      const label = "Last Month Same Day";

      if (data.current_month?.length === 0) {
        return {
          label,
          value: "-",
        };
      }

      const date = data.current_month.at(-1).DATE.slice(-2);

      const value = data.last_month.reduce((prev, curr) => {
        const lastMonthDate = curr.DATE.slice(-2);

        if (date === lastMonthDate) {
          return prev + curr[id];
        }

        return prev;
      }, 0);

      return {
        label,
        value: numDifferentiation(value),
        unformattedValue: value,
        loaclaizedValue: formatToCommaString(value),
      };
    } else {
      const label = "Day Before Yesterday (D-2)";

      if (data.current_month?.length < 2) {
        return {
          label,
          value: "-",
        };
      }

      const lastDay = data.current_month.at(-2)[id];

      return {
        label,
        value: numDifferentiation(lastDay),
        unformattedValue: lastDay,
        loaclaizedValue: formatToCommaString(lastDay),
      };
    }
  };

  const getGrowth = (today, lastday) => {
    const label = "Growth";
    const valueToday = today.unformattedValue;
    const valueYesterday = lastday.unformattedValue;

    if (valueToday == null || valueYesterday == null) {
      return { label, value: "-" };
    }

    const growth =
      valueYesterday === 0
        ? 100
        : (((valueToday - valueYesterday) / valueYesterday) * 100.0).toFixed(2);

    return { label, value: growth + "%" };
  };

  const getAverage = () => {
    const label = "Average Daily";
    let id = paymentValue + paymentTabConfig[paymentTabIndex].id + "_MTD";
    if (
      ["ACTIVE_MERCHANT_ANY_AMT", "ACTIVE_MERCHANT_QUALIFIED"].includes(
        paymentValue
      )
    ) {
      id = paymentValue;
    }

    const value =
      data.current_month.length > 0 ? data.current_month.at(-1)[id] : 0;
    const daysPassed = data.current_month.length;

    const average = daysPassed === 0 ? "-" : value / daysPassed;
    return {
      label,
      value: numDifferentiation(average),
      loaclaizedValue: formatToCommaStringDecPoint(average),
    };
  };

  const config = () => {
    const today = getToday();
    const lastDay = getYesterday();

    const growth = getGrowth(today, lastDay);
    const average = getAverage();

    return [today, lastDay, growth, average];
  };

  const fillIntheGapsForBlankDate = (data) => {
    var temp = [...data];
    const monthLine = [
      "01",
      "02",
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
      "09",
      "10",
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "20",
      "21",
      "22",
      "23",
      "24",
      "25",
      "26",
      "27",
      "28",
      "29",
      "30",
      "31",
    ];
    monthLine.forEach((k) => {
      if (!temp.find((e) => e.name === k))
        temp.push({ name: k, "Last Month": null });
    });
    console.log(temp);
    return temp;
  };

  const generateData = (data) => {
    const id =
      paymentValue +
      paymentTabConfig[paymentTabIndex].id +
      tabConfig[tabIndex].id;
    let result = [];

    data.current_month.forEach((c) => {
      result.push({
        "Current Month": c[id],
        growthPercent: c[`${paymentValue}_GRTH`] ?? null,
        name: c.DATE.slice(-2),
      });
    });

    data.last_month.forEach((c) => {
      const _idx = result.findIndex((r) => r.name === c.DATE.slice(-2));
      if (_idx === -1) {
        result.push({ "Last Month": c[id], name: c.DATE.slice(-2) });
      } else {
        result[_idx]["Last Month"] = c[id];
      }
    });

    const filledDataPoints = fillIntheGapsForBlankDate(result);

    return filledDataPoints.sort((a, b) => parseInt(a.name) - parseInt(b.name));
  };

  return (
    <>
      <DashboardWidgetCard className={classes.card}>
        <DashboardCardHeader
          className={classes.cardHeader}
          label="Performance Overview"
          subLabel={
            data && (
              <p className={classes.updateDate}>
                Updated till - {formatDate(data.last_parsed_time)}
              </p>
            )
          }
        >
          <ToggleButtonsTrasnferList
            config={tabConfig}
            tabIndex={tabIndex}
            onTabChange={onTabChange}
          />
        </DashboardCardHeader>
        <div className={classes.filtersContent}>
          <div className={classes.paymentForm}>
            <SelectInput
              startAdornment={<SavedDocInbox />}
              label="Transaction Type"
              options={options}
              value={paymentValue}
              onChange={(e) => {
                setTabIndex(0);
                setPaymentTabIndex(0);
                setPaymentValue(e.target.value);
              }}
            />
          </div>
          <ToggleButtonsTrasnferList
            config={paymentTabConfig}
            tabIndex={paymentTabIndex}
            onTabChange={onPaymentTabChange}
          />
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          data && (
            <>
              <div className={classes.container}>
                {config().map((c) => {
                  return (
                    <Tooltip
                      classes={{ tooltip: classes.tooltip }}
                      placement="top"
                      title={c.loaclaizedValue ?? "-"}
                    >
                      <div className={classes.widget} key={c.label}>
                        <HeaderAndValue
                          {...c}
                          styles={{ valueClass: { fontSize: 18 } }}
                        />
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <div className={classes.chartContainer}>
                <div className={classes.chartWrapper}>
                  <ResponsiveLineChart data={generateData(data)} />
                </div>
              </div>
            </>
          )
        )}
      </DashboardWidgetCard>
      <DaywiseActiveMerchant merchantData={data} isLoading={isLoading} />
    </>
  );
}
